import React from 'react';
import { Setlist, Button, Modal } from "player-components";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";

import {DownloadIcon, ExclamationIcon} from "@heroicons/react/solid";

import { createEmptySession, selectSetlistSessions, unpackSessionAsync, unpackSetlistAsync, removeSessionFromSetlistAsync, packSetlistAsync } from "../../features/setlist/setlistSlice";

import { openSession, unmountSessionAsync } from "../../features/session/sessionSlice";
import { addNotificationAsync, setIOSModalVisible, selectClientConfig } from "../../features/ui/uiSlice";
import audioEngine from "../../services/audioEngine";
import platform from "platform";
import { format, formatDistance, formatRelative, subDays } from 'date-fns'


function useQuery() {
    const { search } = useLocation();
  
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }


const MySetListContainer = () => {
    const sessions = useSelector(selectSetlistSessions);

    const clientConfiguration = useSelector(selectClientConfig);
    let { setlistId } = useParams();

    const query = useQuery();

    const [sortedSessions, setSortedSessions] = useState([]);
    const [showDownloadSetlistModal, setShowDownloadSetlistModal] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(()=>{
        // console.log(sessions);
        const copiedSessions = [...sessions];
        setSortedSessions(copiedSessions.sort(sortSessions))
    },[sessions]);

    const selectSession = async (sessionId) => {
        
        const selectedSession = sessions.find(session => session.id === sessionId);
        const selectedSessionSongId = selectedSession.song.songId;

        if (
            (clientConfiguration.logic.restrictedDevices.includes(platform.product)) ||
            (platform.os?.family === 'OS X' && navigator.maxTouchPoints)
        ) {
            if (!clientConfiguration.features.library.songsWorkingOnIos.includes(selectedSessionSongId)) {
                dispatch(setIOSModalVisible(true));
            } else {
                await dispatch(openSession(selectedSession));
                navigate(`/session`);
            }
        } else {
            await dispatch(openSession(selectedSession));
            navigate(`/session`);
        }
    }

    const deleteSong = async (sessionId) => {
        await dispatch(unmountSessionAsync());
        await dispatch(removeSessionFromSetlistAsync(sessionId));

		// pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
        /*
		dispatch(addNotificationAsync({
            title: 'Session deleted',
            text: `Session deleted from your setlist`,
            type: 'positive',
        }));
		*/
    }

    const handleUploadSession = async (file) => {
        const sessionZipArrayBuffer = await file.arrayBuffer();
        if (file.name.includes('.setlist')) {
            await dispatch(unpackSetlistAsync(sessionZipArrayBuffer));

			// pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
            /* 
			await dispatch(addNotificationAsync({
                type: 'positive',
                title: 'Complete',
                text: 'Songs were added to your setlist'
            }));
			*/

        }
        if (file.name.includes('.session')) {
            await dispatch(unpackSessionAsync(sessionZipArrayBuffer));

			// pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
			/*
            await dispatch(addNotificationAsync({
                type: 'positive',
                title: 'Complete',
                text: 'Session added to your setlist'
            }));
			*/
        }
    }

    const handleDownloadSetlist = () => {
        setShowDownloadSetlistModal(true);
    }

    const startSetlistDownload = () => {
        setShowDownloadSetlistModal(false);
        dispatch(packSetlistAsync());
    }

    const handleNewSession = (sessionName) => {
        // dispatch(createEmptySession({
        //     title:sessionName
        // }));
    }
    const handleGoToLibrary = () => {
        navigate(`/library`);
    }

    const sortSessions = (a,b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        // console.log(aDate, bDate);
        return aDate < bDate ? 1 : -1;
    }

    const isSongSupported = (songId) => {
        if ( clientConfiguration.logic.restrictedDevices.includes(platform.product) && !clientConfiguration.features.library.songsWorkingOnIos.includes(songId)) {
            return false;
        } else if (platform.os?.family === 'OS X' && navigator.maxTouchPoints) { // is iPad
            if (!clientConfiguration.features.library.songsWorkingOnIos.includes(songId)) {
                return false;
            }
        }
        return true;
    }

    const renderNotSupported = () => {
        return <div className="flex items-center"><ExclamationIcon className="h-4 w-4 mr-2 text-primary" /><span className="text-sm">Not supported on this device</span></div>
    }
    return <>
    <Setlist 
        items={sortedSessions.map(session => {
            return {
                id: session.id,
                title: <><span>{session.song.mix}</span> {!isSongSupported(session.song.songId) ? renderNotSupported(): null}</>,
                artist: session.song.artist,
                genre: session.song.genre,
                decade: session.song.decade,
                key: session.song.key,
                time: session.song.len,
                state: "IN_SETLIST",
                trackCount: session.stems.concat(session.userStems).length,
                lastSavedAt: formatDistance(new Date(session.lastUpdatedAt), new Date(), { addSuffix: true }),
                createdBy: session.createdBy,
                onSelected: ()=>selectSession(session.id),
                onRemoveAction: deleteSong,
                showRemoveActionButton: true,
                removeLabel: 'Remove from setlist'
            }
        })} 
        sortedBy={{
            "label": "Title",
            "dir": "desc"
        }}
        showDownloadButton={(sortedSessions?.length > 0) && clientConfiguration.features.setlist.downloadSetlist}
        showRenameSession={clientConfiguration.features.setlist.renameSession}
        onDownloadSetlist={handleDownloadSetlist}
        showHeader={false}
        title={query.get('title')}
        subTitle={query.get('subtitle')}
        imageUrl={atob(query.get('imageUrl'))}
        // http://localhost:3000/player-application#/setlist?title=dsdfsdf&subtitle=dsfuhdsifsd&imageUrl=aHR0cHM6Ly9pbWFnZXMudW5zcGxhc2guY29tL3Bob3RvLTE1NDcwMzY5NjctMjNkMTFhYWNhZWUwP2l4aWQ9TVh3eE1qQTNmREI4TUh4d2FHOTBieTF3WVdkbGZIeDhmR1Z1ZkRCOGZIdyUzRCZpeGxpYj1yYi0xLjIuMSZhdXRvPWZvcm1hdCZmaXQ9Y3JvcCZ3PTUxMiZxPTgw
        onUpload={handleUploadSession}
        onNewSession={handleNewSession}
        onGoToLibrary={handleGoToLibrary}
        emptyLinkComponent={<Link to={'/library'}><Button>Go to Song library</Button></Link>}
    />
    <Modal 
        isOpen={showDownloadSetlistModal ? true:false}
        title="Download your setlist"
        text={<p>Downloading your setlist will create a <b>.setlist</b> file which you can share with other users. <br/><br/>Any sessions with your own recorded audio will not be included in your download.</p>}
        confirmLabel="Download"
        icon={<DownloadIcon className="h-6 w-6 text-primary" aria-hidden="true" />}
        onConfirm={startSetlistDownload}
        onClose={()=>setShowDownloadSetlistModal(false)}
        closeLabel="Close"
      />
    </>
}

export default MySetListContainer;